import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { AiRegistryItem, AiRegistryKind, AiRegistryUsageMap } from '@azure/models';
import { environment } from 'environments/environment';

/**
 * Catálogo de capacidades que un agente puede usar: servidores MCP y skills.
 * Se direccionan por clave y no por el id de Mongo.
 */
@Injectable({ providedIn: 'root' })
export class AiRegistryService {
  private readonly url = `${environment.azureApiUrl}/api/ai-registry`;

  constructor(private _http: HttpClient) {}

  /**
   * El catálogo completo, o solo una clase.
   * @param kind `mcp` o `skill`; sin valor devuelve todo
   */
  getAll(kind?: AiRegistryKind) {
    const url = kind ? `${this.url}?kind=${kind}` : this.url;
    return this._http.get<AiRegistryItem[]>(url).pipe(
      map((resp) => resp as AiRegistryItem[]),
      catchError((e) => throwError(e))
    );
  }

  /** Solo los activos: para los selectores. El catálogo usa getAll(). */
  getActive() {
    return this._http.get<AiRegistryItem[]>(`${this.url}/active`).pipe(
      map((resp) => resp as AiRegistryItem[]),
      catchError((e) => throwError(e))
    );
  }

  getByKey(key: string) {
    return this._http.get<AiRegistryItem>(`${this.url}/${key}`).pipe(
      map((resp) => resp as AiRegistryItem),
      catchError((e) => throwError(e))
    );
  }

  /** El uso de todo el catálogo en una llamada; de a uno serían dos docenas. */
  getUsageAll() {
    return this._http.get<AiRegistryUsageMap>(`${this.url}/usage`).pipe(
      map((resp) => resp || {}),
      catchError((e) => throwError(e))
    );
  }

  /** Nombres de los agentes que la usan; es lo que impide eliminarla. */
  getUsage(key: string) {
    return this._http.get<string[]>(`${this.url}/${key}/usage`).pipe(
      map((resp) => resp as string[]),
      catchError((e) => throwError(e))
    );
  }

  create(item: AiRegistryItem) {
    return this._http.post<AiRegistryItem>(this.url, item).pipe(
      map((resp) => resp as AiRegistryItem),
      catchError((e) => throwError(e))
    );
  }

  /** La clave del cuerpo se ignora en el servidor: se edita la de la ruta. */
  update(key: string, item: AiRegistryItem) {
    return this._http.put<AiRegistryItem>(`${this.url}/${key}`, item).pipe(
      map((resp) => resp as AiRegistryItem),
      catchError((e) => throwError(e))
    );
  }

  /** No se eliminan desde la web: se desactivan y se pueden volver a activar. */
  enable(key: string) {
    return this._http.put<AiRegistryItem>(`${this.url}/${key}/enable`, {}).pipe(
      map((resp) => resp as AiRegistryItem),
      catchError((e) => throwError(e))
    );
  }

  disable(key: string) {
    return this._http.put<AiRegistryItem>(`${this.url}/${key}/disable`, {}).pipe(
      map((resp) => resp as AiRegistryItem),
      catchError((e) => throwError(e))
    );
  }
}
